import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from "redux";
import uuid from "uuid";

import {Button, Card, Col, Divider, Form, Input, InputNumber, notification, Row, Select} from 'antd';

import {addProduct, generateBarcode} from './actions';
import {getCategoryTree} from "../Category/actions";
import {getDescriptionFromError} from "../utils/errors";
import {getBrands} from "../Brand/actions";
import {getSuppliers} from "../Supplier/actions";
import {getRegulatoryReportingTypes} from "../RegulatoryReporting/actions";
import {formItemLayout} from "../utils/style";

import UploadProductImage from "./UploadProductImage";
import units from "./units";
import SelectCategoryTree from "../Category/SelectCategoryTree";

const FormItem = Form.Item;
const Option = Select.Option;
const TextArea = Input.TextArea;
const ButtonGroup = Button.Group;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

class AddProductForm extends Component {
  static propTypes = {
    loading: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    addProduct: PropTypes.func.isRequired,
    getCategoryTree: PropTypes.func.isRequired,
    getBrands: PropTypes.func.isRequired,
    dispensaryId: PropTypes.string.isRequired,
    categoryTree: PropTypes.object.isRequired,
    brands: PropTypes.array.isRequired,
    getSuppliers: PropTypes.func.isRequired,
    suppliers: PropTypes.array.isRequired,
    generateBarcode: PropTypes.func.isRequired,
    generatedBarcode: PropTypes.string,
    getRegulatoryReportingTypes: PropTypes.func.isRequired,
    regulatoryReportingTypes: PropTypes.array.isRequired,
    units: PropTypes.array.isRequired,
  };
  static defaultProps = {
    units
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this._load();
  }

  componentDidUpdate(prevProps) {
    if (this.props.dispensaryId !== prevProps.dispensaryId) this._load();
  }

  _load() {
    const {dispensaryId} = this.props;
    this.props.getCategoryTree({dispensaryId});
    this.props.getBrands({dispensaryId});
    this.props.getSuppliers({dispensaryId});
    this.props.getRegulatoryReportingTypes();
    this.setState({productGuid: uuid.v4()});
  }

  goBack = () => {
    this.props.history.goBack();
  };

  handleSubmit = (e) => {
    const {dispensaryId} = this.props;
    e.preventDefault();
    this.props.form.validateFieldsAndScroll(async(err, values) => {
      if (err) return;
      try {
        const product = {...values, dispensaryId};
        await this.props.addProduct(product);
        this.goBack();
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        });
      }
    });
  };

  handleGenerateNewBarcode = async() => {
    const {dispensaryId, generateBarcode} = this.props;
    try {
      await generateBarcode({dispensaryId});
      const {generatedBarcode, form: {setFieldsValue}} = this.props;
      setFieldsValue({initialSku: generatedBarcode});
    } catch (e) {
      notification.error({
        message: 'Failed',
        description: 'Generating barcode'
      });
    }
  };

  _getUnitOfMeasureClassFor(regulatoryReportingTypeId) {
    const {regulatoryReportingTypes} = this.props;
    const found = regulatoryReportingTypes.find(x => x.regulatoryReportingTypeId === regulatoryReportingTypeId);
    return (found && found.unitOfMeasureClass) || null;
  }

  _onRegulatoryReportingTypeChange = (value) => {
    const {form: {getFieldValue, setFieldsValue}, units} = this.props;
    const newUnitOfMeasureClass = this._getUnitOfMeasureClassFor(value);
    const unitValue = getFieldValue('unit');
    const unit = unitValue && units.find(x => x.value === unitValue);
    if (unit && unit.unitOfMeasureClass !== newUnitOfMeasureClass) {
      setFieldsValue({unit: null});
    }
  };

  render() {
    const {
      brands, categoryTree, form: {getFieldDecorator, getFieldsError, getFieldValue}, loading,
      suppliers, regulatoryReportingTypes, units,
    } = this.props;
    const regulatoryReportingTypeId = getFieldValue('regulatoryReportingTypeId');
    const selectedUnitOfMeasureClass = this._getUnitOfMeasureClassFor(regulatoryReportingTypeId);
    const availableUnits = (selectedUnitOfMeasureClass && units.filter(x => x.unitOfMeasureClass === selectedUnitOfMeasureClass)) || units;
    const {productGuid} = this.state;
    const quantityPresent = getFieldValue('initialQuantity') !== undefined;
    return (
      <Card title="Add Product">
        <Form onSubmit={e => e.preventDefault()}>
          <FormItem
            {...formItemLayout}
            label="Category"
          >
            {getFieldDecorator('categoryId', {
              rules: [{
                required: true,
                message: 'Category is required'
              }]
            })(
              <SelectCategoryTree tree={categoryTree} hideNone />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Brand"
          >
            {getFieldDecorator('brandId', {
              initialValue: null
            })(
              <Select>
                <Option key="addProduct-brandId-null" value={null}>NONE</Option>
                {brands.map(brand =>
                  <Option key={brand.brandId} value={brand.brandId}>{brand.name}</Option>
                )}
              </Select>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Supplier"
          >
            {getFieldDecorator('supplierId', {
              initialValue: null
            })(
              <Select>
                <Option key="addProduct-supplierId-null" value={null}>NONE</Option>
                {suppliers.map(supplier =>
                  <Option key={supplier.supplierId} value={supplier.supplierId}>{supplier.name}</Option>
                )}
              </Select>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Product ID"
          >
            {getFieldDecorator('productGuid', {
              initialValue: productGuid,
              rules: [{
                required: true,
                message: 'Product ID is required'
              }]
            })(
              <Input />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Regulatory Reporting Type"
          >
            {getFieldDecorator('regulatoryReportingTypeId', {
              initialValue: null
            })(
              <Select onChange={this._onRegulatoryReportingTypeChange}>
                <Option key={"regulatory-reporting-type-none"} value={null}>NONE</Option>
                {regulatoryReportingTypes.map(rrt =>
                  <Option key={rrt.regulatoryReportingTypeId} value={rrt.regulatoryReportingTypeId}>
                    {rrt.name}
                  </Option>
                )}
              </Select>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Name"
          >
            {getFieldDecorator('name', {
              rules: [{
                required: true,
                message: 'Name is required'
              }]
            })(
              <Input />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Description"
          >
            {getFieldDecorator('description', {
              initialValue: ''
            })(
              <TextArea />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Package quantity"
          >
            {getFieldDecorator('packageQuantity', {
              rules: [{
                required: true,
                message: 'Package quantity is required'
              }, {
                validator(rule, value, callback) {
                  const errors = [];
                  if (value !== '' && value <= 0) {
                    errors.push(new Error('must be greater than 0'));
                  }
                  callback(errors);
                }
              }]
            })(
              <InputNumber precision={3} />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Unit"
          >
            {getFieldDecorator('unit', {
              rules: [{
                required: true
              }]
            })(
              <Select>
                {availableUnits
                  .map(({value, display}) => <Option key={value} value={value}>{display}</Option>)
                }
              </Select>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Value counting towards daily limit"
          >
            {getFieldDecorator('limitValue', {
              rules: [
                {type: 'number', min: 0, required: !!getFieldValue('regulatoryReportingTypeId')}
              ]
            })(
              <InputNumber precision={3} />
            )}
            &nbsp;gram(s)
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Sale Price"
          >
            {getFieldDecorator('salePrice', {
              rules: [{
                required: true,
                message: 'Sale price is required'
              }, {
                type: 'number',
                min: 0,
                message: 'Must be a positive number'
              }]
            })(
              <InputNumber precision={3} />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Supplier Order Code"
          >
            {getFieldDecorator('supplierOrderCode', {initialValue: null})(
              <Input />
            )}
          </FormItem>
          <Divider orientation="left">Initial Stock</Divider>
          <FormItem
            {...formItemLayout}
            label="Quantity"
          >
            {getFieldDecorator('initialQuantity', {
              rules: [{
                type: 'number',
                min: 0,
                message: 'Must be a positive number'
              }]
            })(
              <InputNumber precision={0} />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="SKU"
          >
            <Row gutter={16}>
              <Col span={16}>
                {getFieldDecorator('initialSku', {
                  rules: [{
                    required: quantityPresent,
                    message: 'SKU is required'
                  }]
                })(
                  <Input />
                )}
              </Col>
              <Col span={8}>
                <Button onClick={this.handleGenerateNewBarcode}>Generate</Button>
              </Col>
            </Row>
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Lot Number"
          >
            {getFieldDecorator('initialLotNumber')(
              <Input />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Net Cost Per Unit"
          >
            {getFieldDecorator('netCostPerUnit', {
              rules: [{
                required: quantityPresent,
                message: 'Net cost per unit is required'
              }, {
                type: 'number',
                min: 0,
                message: 'Must be a positive number'
              }]
            })(
              <InputNumber/>
            )}
          </FormItem>
          <Divider />
          <FormItem
            {...formItemLayout}
            label="Image"
          >
            {getFieldDecorator('imageUri', {initialValue: null})(
              <UploadProductImage />
            )}
          </FormItem>
          <FormItem>
            <ButtonGroup>
              <Button
                type="primary"
                htmlType="button"
                onClick={this.handleSubmit}
                disabled={loading || hasErrors(getFieldsError())}
              >
                Add Product
              </Button>
              <Button
                htmlType="button"
                onClick={this.goBack}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </FormItem>
        </Form>
      </Card>
    );
  }
}

const AddProductPage = Form.create()(AddProductForm);

const mapStateToProps = state => ({
  loading: state.category.categoriesLoading || state.brand.brandsLoading || state.supplier.gettingSuppliers
    || state.product.generatingBarcode || state.product.addingProduct,
  dispensaryId: state.dispensary.selectedDispensaryId,
  categoryTree: state.category.categoryTree,
  brands: state.brand.brands,
  suppliers: state.supplier.suppliers,
  generatedBarcode: state.product.generatedBarcode,
  regulatoryReportingTypes: state.regulatoryReportingType.regulatoryReportingTypes,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  addProduct,
  getCategoryTree,
  getBrands,
  getSuppliers,
  getRegulatoryReportingTypes,
  generateBarcode,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AddProductPage);



// WEBPACK FOOTER //
// ./src/Product/AddProductPage.js